import { useEffect, useRef, useState } from "react"
import { Marker } from "react-map-gl"
import { resolveEventLngLat } from "plugins/resolveLngLat"
import { LngLatCoordinatePair } from "features/map/event/types"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { getGlobalMapRef, getViewport } from "features/map/GlobalViewState"
import store from "integrations/redux/store"
import { customMapController, disabledMapController } from "features/map/control/slice"
import { useAppSelector, useAppDispatch } from "integrations/redux/hooks"
import {
    selectIsDrawingOrthoMask,
    selectOrthoMaskDrawingType,
    setDrawingType,
} from "./OrthoMaskSlice"

function unprojectScreenPoint(x: number, y: number): LngLatCoordinatePair {
    const mapRef = getGlobalMapRef()?.current?.getMap()
    const lngLat = mapRef?.unproject?.([x, y])
    if (lngLat) return [lngLat.lng, lngLat.lat]
    const [lng, lat] = getViewport().unproject([x, y])
    return [lng, lat]
}

export function OrthoMaskDrawingOverlay() {
    const isDrawing = useAppSelector(selectIsDrawingOrthoMask)
    const drawingType = useAppSelector(selectOrthoMaskDrawingType)
    const points = geometryDataStore.useGeometryData("points")
    const [tooltipCoords, setTooltipCoords] = useState<LngLatCoordinatePair | undefined>()
    const dragStartRef = useRef<{ x: number; y: number; rect: DOMRect } | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const dispatch = useAppDispatch()

    // Tooltip for polygon mode (show only when no points placed yet)
    useEffect(() => {
        if (!isDrawing || drawingType !== "polygon") {
            setTooltipCoords(undefined)
            return
        }
        const tooltipText = points.length === 0 ? "Click to add points" : null
        if (!tooltipText) {
            setTooltipCoords(undefined)
            return
        }
        const map = document.getElementById("map")
        const listener = (event: MouseEvent) => {
            if (
                (event.target as HTMLElement)?.className === "maplibregl-canvas" &&
                !event.buttons
            ) {
                const coordinate = resolveEventLngLat(event)
                setTooltipCoords([coordinate.longitude, coordinate.latitude])
            } else {
                setTooltipCoords(undefined)
            }
        }
        map?.addEventListener("mousemove", listener)
        return () => {
            map?.removeEventListener("mousemove", listener)
            setTooltipCoords(undefined)
        }
    }, [isDrawing, drawingType, points.length])

    // Rectangle drawing mode: canvas overlay + mouse event handling
    useEffect(() => {
        if (!isDrawing || drawingType !== "rectangle") return

        const mapEl = document.getElementById("map")
        if (!mapEl) return

        const canvas = document.createElement("canvas")
        canvas.style.cssText =
            "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;"
        canvas.width = mapEl.offsetWidth
        canvas.height = mapEl.offsetHeight
        mapEl.appendChild(canvas)
        canvasRef.current = canvas

        const syncCanvasSize = () => {
            if (canvas.width !== mapEl.offsetWidth) canvas.width = mapEl.offsetWidth
            if (canvas.height !== mapEl.offsetHeight) canvas.height = mapEl.offsetHeight
        }

        const drawRectPreview = (
            x1: number,
            y1: number,
            x2: number,
            y2: number,
            isShift: boolean
        ) => {
            const ctx = canvas.getContext("2d")
            if (!ctx) return
            syncCanvasSize()
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            let ex = x2,
                ey = y2
            if (isShift) {
                const size = Math.max(Math.abs(ex - x1), Math.abs(ey - y1))
                ex = x1 + (ex > x1 ? size : -size)
                ey = y1 + (ey > y1 ? size : -size)
            }

            const rx = Math.min(x1, ex)
            const ry = Math.min(y1, ey)
            const rw = Math.abs(ex - x1)
            const rh = Math.abs(ey - y1)

            ctx.strokeStyle = "#27BBFF"
            ctx.fillStyle = "rgba(39,187,255,0.25)"
            ctx.lineWidth = 2
            ctx.beginPath()
            ctx.rect(rx, ry, rw, rh)
            ctx.fill()
            ctx.stroke()
        }

        const handleMouseDown = (e: MouseEvent) => {
            if ((e.target as HTMLElement)?.className !== "maplibregl-canvas") return
            const rect = (e.target as HTMLElement).getBoundingClientRect()
            dragStartRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
                rect,
            }
            store.dispatch(disabledMapController())
        }

        const handleMouseMove = (e: MouseEvent) => {
            const start = dragStartRef.current
            if (!start) return
            const x = e.clientX - start.rect.left
            const y = e.clientY - start.rect.top
            drawRectPreview(start.x, start.y, x, y, e.shiftKey)
        }

        const handleMouseUp = (e: MouseEvent) => {
            const start = dragStartRef.current
            if (!start) return

            const ctx = canvas.getContext("2d")
            ctx?.clearRect(0, 0, canvas.width, canvas.height)

            const x1 = start.x,
                y1 = start.y
            let x2 = e.clientX - start.rect.left,
                y2 = e.clientY - start.rect.top

            if (e.shiftKey) {
                const size = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1))
                x2 = x1 + (x2 > x1 ? size : -size)
                y2 = y1 + (y2 > y1 ? size : -size)
            }

            const minX = Math.min(x1, x2)
            const maxX = Math.max(x1, x2)
            const minY = Math.min(y1, y2)
            const maxY = Math.max(y1, y2)

            // Only commit if the drag had meaningful size
            if (maxX - minX > 4 && maxY - minY > 4) {
                const [tl, tr, br, bl] = [
                    unprojectScreenPoint(minX, minY),
                    unprojectScreenPoint(maxX, minY),
                    unprojectScreenPoint(maxX, maxY),
                    unprojectScreenPoint(minX, maxY),
                ]
                geometryDataStore.setAll([
                    { longitude: tl[0], latitude: tl[1] },
                    { longitude: tr[0], latitude: tr[1] },
                    { longitude: br[0], latitude: br[1] },
                    { longitude: bl[0], latitude: bl[1] },
                ])
                dispatch(setDrawingType("edit"))
            }

            dragStartRef.current = null
            store.dispatch(
                customMapController({
                    options: { dragRotate: false, doubleClickZoom: false },
                })
            )
        }

        mapEl.addEventListener("mousedown", handleMouseDown)
        window.addEventListener("mousemove", handleMouseMove)
        window.addEventListener("mouseup", handleMouseUp)

        return () => {
            canvas.remove()
            canvasRef.current = null
            dragStartRef.current = null
            mapEl.removeEventListener("mousedown", handleMouseDown)
            window.removeEventListener("mousemove", handleMouseMove)
            window.removeEventListener("mouseup", handleMouseUp)
        }
    }, [isDrawing, drawingType, dispatch])

    // Tooltip for rectangle mode
    useEffect(() => {
        if (!isDrawing || drawingType !== "rectangle") {
            if (drawingType !== "polygon") setTooltipCoords(undefined)
            return
        }
        const map = document.getElementById("map")
        const listener = (event: MouseEvent) => {
            if (
                (event.target as HTMLElement)?.className === "maplibregl-canvas" &&
                !event.buttons
            ) {
                const coordinate = resolveEventLngLat(event)
                setTooltipCoords([coordinate.longitude, coordinate.latitude])
            } else {
                setTooltipCoords(undefined)
            }
        }
        map?.addEventListener("mousemove", listener)
        return () => {
            map?.removeEventListener("mousemove", listener)
            setTooltipCoords(undefined)
        }
    }, [isDrawing, drawingType])

    if (!isDrawing || !tooltipCoords) return null

    const tooltipText =
        drawingType === "rectangle"
            ? "Click (and drag) to draw area"
            : points.length === 0
              ? "Click to add points"
              : null

    if (!tooltipText) return null

    return (
        <Marker
            longitude={tooltipCoords[0]}
            latitude={tooltipCoords[1]}
            anchor="top-left"
            offset={[4, 8]}
            style={{ pointerEvents: "none" }}
        >
            <div className="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                <small>{tooltipText}</small>
            </div>
        </Marker>
    )
}
